// src/marketing/GuideModal.jsx
// A plain-language guide for parents (and students) explaining what Writing
// Coach does, how it works, and what to expect. Reused from both the public
// LandingPage and the logged-in WritingCoach app — same modal, same content.
export default function GuideModal({ onClose }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #21262d" }}>
          <div>
            <h2 style={headingStyle}>Parent &amp; Student Guide</h2>
            <p style={{ color: "#8b949e", fontSize: 12, margin: 0 }}>What to expect from Writing Coach</p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        <div style={bodyStyle}>

          <Section title="What Writing Coach Does">
            Writing Coach is an AI-powered writing evaluator for students in grades 2–12. It reads a
            piece of writing and returns coach-style feedback, color-coded inline annotations, and a
            structure report based on a rubric calibrated to your child's grade.
            {"\n\n"}
            <strong style={{ color: "#c9d1d9" }}>It only evaluates.</strong> Writing Coach never rewrites,
            completes, or generates content for your child's assignment — it points out what's working
            and what to improve, and leaves the writing and the revising to them.
          </Section>

          <Section title="How It Works">
            1. Pick a grade level and a writing mode — Single Paragraph, 5-Paragraph Essay, Creative
            Narrative, or Unstructured / Free Write.
            {"\n\n"}
            2. Submit the writing — type it directly, upload a PDF/.docx/.txt file, or take a photo of
            handwritten work (up to about 1200 words).
            {"\n\n"}
            3. Writing Coach evaluates it against a grade-calibrated rubric, usually in well under a
            minute.
            {"\n\n"}
            4. Your child reviews the feedback and revises on their own — there's no "accept changes"
            button, because there are no changes to accept.
          </Section>

          <Section title="What Your Child Will See">
            Every evaluation returns three views: <strong style={{ color: "#c9d1d9" }}>Coach Feedback</strong>{" "}
            (encouraging, specific commentary — strengths and opportunities), <strong style={{ color: "#c9d1d9" }}>Annotated
            Writing</strong> (the original text with color-coded highlights they can tap for a comment), and a{" "}
            <strong style={{ color: "#c9d1d9" }}>Structure Report</strong> (a rubric breakdown by criterion).
            {"\n\n"}
            They can also paste in the actual assignment prompt to see how well their writing matches it,
            and download a text report of the full result to keep or share with a teacher.
          </Section>

          <Section title="While They're Drafting">
            Before spending a full evaluation, students can use three quick on-demand tools: a grammar
            check, sentence-level feedback on a highlighted passage, and revision ideas. Like the main
            evaluation, these only point out issues and coach toward a fix — they never hand back
            corrected or rewritten text.
          </Section>

          <Section title="Tokens & Pricing">
            Writing Coach runs on a simple token system: 1 token per evaluation (2 for submissions over
            800 words), and each quick draft-tool check is 1 token. Adding a teacher's rubric to an
            evaluation adds a flat +1 token. New accounts start with 1 free token to try it out, and more
            can be purchased anytime from the token balance in the app header. Tokens never expire.
          </Section>

          <Section title="Privacy & Your Child's Writing">
            Submitted writing is sent to Anthropic's Claude API only to generate the evaluation —
            Writing Coach does not keep a copy of the writing itself once the result comes back. We're
            FERPA-aware for classroom use and COPPA-conscious for families: children under 13 use the
            app only with a parent, guardian, or educator's consent at signup. See the full Privacy
            Policy and Terms of Service for details.
          </Section>

          <Section title="Supported File Types">
            Type directly into the app, or upload a PDF, .docx, or .txt file. You can also submit a
            photo of handwritten work (JPG, PNG, or WEBP) — Writing Coach reads it using Claude's vision
            model, no separate scanning software needed.
          </Section>

          <Section title="A Note on Academic Honesty">
            Writing Coach is built for students to submit their own original work. Using another AI tool
            to generate or rewrite a submission and presenting it as a student's own writing isn't
            supported here, and defeats the purpose of getting feedback in the first place.
          </Section>

          <Section title="Questions?">
            Email us anytime at <a href="mailto:support@chaicodes.com" style={linkStyle}>support@chaicodes.com</a>.
          </Section>
        </div>
      </div>
    </div>
  );
}

// ── Sub-component ─────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", margin: "0 0 6px" }}>{title}</h3>
      <p style={{ fontSize: 13, color: "#8b949e", margin: 0, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
        {children}
      </p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlayStyle = {
  position:       "fixed",
  inset:          0,
  background:     "rgba(0,0,0,0.85)",
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  zIndex:         300,
  padding:        "20px",
};

const modalStyle = {
  width:        "100%",
  maxWidth:     620,
  maxHeight:    "90vh",
  background:   "#161b22",
  border:       "1px solid #30363d",
  borderRadius: 16,
  padding:      "28px",
  display:      "flex",
  flexDirection: "column",
  fontFamily:   "'DM Sans', sans-serif",
};

const headingStyle = {
  fontFamily: "'DM Serif Display', serif",
  fontSize:   22,
  fontWeight: 400,
  color:      "#e6edf3",
  margin:     0,
};

const bodyStyle = {
  overflowY: "auto",
  flex:      1,
  marginBottom: 8,
  paddingRight: 4,
};

const closeBtnStyle = {
  background:     "transparent",
  border:         "1px solid #30363d",
  color:          "#8b949e",
  borderRadius:   7,
  width:          32,
  height:         32,
  cursor:         "pointer",
  fontSize:       16,
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  flexShrink:     0,
};

const linkStyle = { color: "#388bfd", textDecoration: "none" };
